import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Menu, X, Instagram, ArrowRight, Building2, Users, Award, 
  TrendingUp, Briefcase, Sparkles, Calendar, Gift, 
  CheckCircle2, MessageCircle, ShieldCheck, BadgeCheck, Package
} from 'lucide-react';
import { products, contactInfo } from '../../mock';
import Official from '../../components/Official';
import Trust from '../../components/Trust';
import Footer from '../../components/Footer';
import { trackB2BQuoteRequest, trackWhatsAppClick } from '../../lib/analytics';

const CAMPAIGN_SOURCE = 'empresas';

// Volume pricing tiers (base price $30)
const PRICING_TIERS = [
  { min: 10, max: 49, unitPrice: 23.25, discount: 22.5, label: '-22.5%' },
  { min: 50, max: 99, unitPrice: 21.75, discount: 27.5, label: '-27.5%' },
  { min: 100, max: 150, unitPrice: 18.75, discount: 37.5, label: '-37.5%' }
];

const getPricingForQuantity = (qty) => {
  return PRICING_TIERS.find(t => qty >= t.min && qty <= t.max) || PRICING_TIERS[0];
};

export const Empresas = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quantity, setQuantity] = useState(50);
  const [contactName, setContactName] = useState('');
  const [company, setCompany] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [need, setNeed] = useState('');

  useEffect(() => {
    sessionStorage.setItem('campaign_source', CAMPAIGN_SOURCE);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const currentTier = useMemo(() => getPricingForQuantity(quantity), [quantity]);
  const totalPrice = useMemo(() => (currentTier.unitPrice * quantity).toFixed(2), [quantity, currentTier]);
  const savings = useMemo(() => ((30 - currentTier.unitPrice) * quantity).toFixed(2), [quantity, currentTier]);

  const isFormValid = contactName.trim().length > 0 && quantity >= 10;

  const handleWhatsAppContact = () => {
    if (!isFormValid) return;

    // Analytics
    trackB2BQuoteRequest({
      quantity,
      totalValue: parseFloat(totalPrice),
      unitPrice: currentTier.unitPrice,
      discountPct: currentTier.discount,
    });
    trackWhatsAppClick({
      location: 'empresas-quote',
      source: CAMPAIGN_SOURCE,
      value: parseFloat(totalPrice),
      quantity,
    });

    const lines = [
      `Hola! Soy *${contactName}* y quiero cotizar Gafas La Tri para empresa.`,
      ``,
      `📦 Cantidad: *${quantity} unidades*`,
      `💰 Precio unitario estimado: $${currentTier.unitPrice}`,
      `💵 Total estimado: *$${totalPrice}*`,
      currentTier.discount > 0 ? `🎁 Descuento aplicado: ${currentTier.discount}%` : '',
    ];
    
    if (company) lines.push(`🏢 Empresa: ${company}`);
    if (city) lines.push(`📍 Ciudad: ${city}`);
    if (phone) lines.push(`📞 Teléfono: ${phone}`);
    if (need) lines.push(`💼 Necesidad: ${need}`);
    
    lines.push(``, `Quedo atento a su propuesta comercial. Gracias.`);
    
    const message = lines.filter(l => l !== '').join('\n');
    window.open(`${contactInfo.whatsappLink}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const benefits = [
    { icon: ShieldCheck, title: "Protección UV400", desc: "Un regalo que sí van a usar al aire libre." },
    { icon: Package, title: "Presentación premium", desc: "Caja y funda que elevan la experiencia de marca." },
    { icon: TrendingUp, title: "Pedidos desde 10 pares", desc: "Ideal para eventos, promociones y kits corporativos." },
    { icon: Briefcase, title: "Atención dedicada", desc: "Acompañamiento en todo el proceso de compra." }
  ];

  const useCases = [
    { icon: Users, text: "Bienvenida a clientes clave." },
    { icon: Calendar, text: "Regalos en eventos y congresos." },
    { icon: Award, text: "Incentivos para equipos comerciales." },
    { icon: Gift, text: "Kits de bienvenida o aniversario." }
  ];

  const modelCopy = {
    Amarillo: "Máximo impacto visual y energía.",
    Azul: "Estilo sobrio con presencia institucional.",
    Rojo: "Personalidad fuerte y recordación inmediata."
  };

  return (
    <div className="min-h-screen bg-white">
      {/* HEADER */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_13e5738d-7aa3-4072-b101-9672dafbd61a/artifacts/j65cu3zm_logo.jpg" 
                alt="Gafas La Tri" 
                className="h-14 w-auto object-contain"
                loading="eager"
                fetchpriority="high"
                width="120"
                height="56"
              />
              <div className="hidden lg:block border-l border-slate-200 pl-3">
                <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">B2B Corporate</div>
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-7">
              <button onClick={() => scrollToSection('beneficios')} className="text-slate-700 hover:text-blue-900 font-medium transition-colors text-sm" data-testid="nav-beneficios">Beneficios</button>
              <button onClick={() => scrollToSection('volumen')} className="text-slate-700 hover:text-blue-900 font-medium transition-colors text-sm" data-testid="nav-volumen">Volumen</button>
              <button onClick={() => scrollToSection('modelos')} className="text-slate-700 hover:text-blue-900 font-medium transition-colors text-sm" data-testid="nav-modelos">Modelos</button>
              <button onClick={() => scrollToSection('contacto')} className="text-slate-700 hover:text-blue-900 font-medium transition-colors text-sm" data-testid="nav-contacto">Contacto</button>
              <button 
                onClick={() => scrollToSection('contacto')} 
                data-testid="header-cta-cotizar"
                className="bg-blue-900 text-white px-5 py-2.5 rounded-md font-semibold text-sm hover:bg-blue-800 hover:shadow-md transition-all"
              >
                Solicitar cotización
              </button>
            </nav>

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-slate-700" aria-label="Menu">
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200">
              <nav className="flex flex-col space-y-3">
                <button onClick={() => scrollToSection('beneficios')} className="text-left text-slate-700 font-medium py-2">Beneficios</button>
                <button onClick={() => scrollToSection('volumen')} className="text-left text-slate-700 font-medium py-2">Volumen</button>
                <button onClick={() => scrollToSection('modelos')} className="text-left text-slate-700 font-medium py-2">Modelos</button>
                <button onClick={() => scrollToSection('contacto')} className="text-left text-slate-700 font-medium py-2">Contacto</button>
                <button onClick={() => scrollToSection('contacto')} className="bg-blue-900 text-white px-5 py-3 rounded-md font-semibold text-sm">Solicitar cotización</button>
              </nav>
            </div>
          )}
        </div>
      </header>

      <main className="pt-20">
        {/* HERO */}
        <section className="relative bg-gradient-to-br from-slate-50 via-blue-50/30 to-white overflow-hidden">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/40 rounded-full blur-3xl"></div>
          
          <div className="container mx-auto px-4 lg:px-8 py-20 lg:py-28 relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-blue-900 text-white px-4 py-2 rounded-md text-xs font-bold tracking-wider uppercase mb-8">
                  <Building2 size={14} />
                  Programa Corporativo B2B
                </div>
                
                <h1 className="text-5xl lg:text-6xl font-black text-slate-900 mb-6 leading-[1.05] tracking-tight">
                  Regalos corporativos con
                  <span className="block text-blue-900 mt-2">identidad ecuatoriana</span>
                </h1>
                
                <p className="text-lg lg:text-xl text-slate-600 mb-10 leading-relaxed max-w-xl">
                  Sorprende a tus clientes y equipos con gafas oficiales de La Tri, listas para entregar y disponibles desde <strong className="text-blue-900">10 unidades</strong>.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-10">
                  <button 
                    onClick={() => scrollToSection('contacto')}
                    data-testid="hero-cta-cotizar"
                    className="bg-blue-900 text-white px-8 py-4 rounded-md font-bold hover:bg-blue-800 hover:shadow-xl transition-all inline-flex items-center justify-center gap-2"
                  >
                    Solicitar cotización
                    <ArrowRight size={18} />
                  </button>
                  <a
                    href={contactInfo.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-cta-whatsapp"
                    className="border-2 border-slate-300 hover:border-blue-900 text-slate-700 hover:text-blue-900 px-8 py-4 rounded-md font-bold transition-all inline-flex items-center justify-center gap-2"
                  >
                    <MessageCircle size={18} />
                    Cotizar por WhatsApp
                  </a>
                </div>

                {/* Trust badges */}
                <div className="flex flex-wrap gap-6 pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2">
                    <BadgeCheck className="text-blue-900" size={20} />
                    <span className="text-sm font-semibold text-slate-700">Producto Oficial FEF</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="text-blue-900" size={20} />
                    <span className="text-sm font-semibold text-slate-700">Logística probada</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="text-blue-900" size={20} />
                    <span className="text-sm font-semibold text-slate-700">Descuento por volumen</span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_sunglass-hub-6/artifacts/rmsv3ou9_gafas-de-la-tri-colores-frente.jpg"
                  alt="Packaging premium - Gafas La Tri con caja y funda oficial"
                  className="rounded-2xl shadow-2xl w-full object-cover aspect-square"
                  loading="eager"
                  fetchpriority="high"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-5 border border-slate-100">
                  <div className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-1">Disponible desde</div>
                  <div className="text-3xl font-black text-blue-900">10 unidades</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PRODUCTO OFICIAL CON LICENCIA */}
        <Official />

        {/* MERGED SECTION: Tres colores + Mejor precio a mayor volumen */}
        <section id="volumen" className="py-20 bg-gradient-to-br from-blue-50 to-slate-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-5xl mx-auto">
              {/* Heading */}
              <div className="text-center mb-12">
                <div className="text-xs text-blue-900 font-bold tracking-[0.2em] uppercase mb-3">Cotizador rápido</div>
                <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-4 tracking-tight">
                  Tres colores, la misma pasión.
                  <span className="block mt-2 text-blue-900">Mejor precio a mayor volumen.</span>
                </h2>
                <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                  Elige el modelo que mejor se alinea con tu marca y desliza para ver el precio estimado. <strong className="text-blue-900">Descuentos desde 10 unidades</strong>.
                </p>
              </div>

              {/* Compact color models row */}
              <div id="modelos" className="grid grid-cols-3 gap-4 md:gap-6 mb-10">
                {products.map((product) => (
                  <div
                    key={product.id}
                    data-testid={`b2b-model-${product.color.toLowerCase()}`}
                    className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all border border-slate-100"
                  >
                    <div className="aspect-square overflow-hidden bg-slate-50">
                      <img
                        src={product.image}
                        alt={`Modelo ${product.color}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="p-3 md:p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-3 h-3 rounded-full border border-slate-200" style={{ backgroundColor: product.colorHex }}></div>
                        <h3 className="text-sm md:text-base font-bold text-slate-900">{product.color}</h3>
                      </div>
                      <p className="hidden md:block text-xs text-slate-600 leading-snug">{modelCopy[product.color]}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pricing tiers preview */}
              <div className="grid grid-cols-3 gap-3 mb-10">
                {PRICING_TIERS.map((tier, idx) => {
                  const isActive = currentTier.min === tier.min;
                  return (
                    <div
                      key={idx}
                      data-testid={`tier-${tier.min}`}
                      className={`text-center p-4 rounded-lg border-2 transition-all ${
                        isActive
                          ? 'bg-blue-900 text-white border-blue-900 shadow-lg scale-105'
                          : 'bg-white text-slate-700 border-slate-200'
                      }`}
                    >
                      <div className="text-xs uppercase tracking-wider font-bold mb-1 opacity-80">
                        {tier.min} - {tier.max} u
                      </div>
                      <div className="text-2xl font-black">${tier.unitPrice}</div>
                      {tier.discount > 0 && (
                        <div className={`text-xs font-bold mt-1 ${isActive ? 'text-yellow-400' : 'text-green-600'}`}>
                          {tier.label}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Slider Card */}
              <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12 border border-slate-100">
                <div className="mb-8">
                  <div className="flex justify-between items-end mb-4">
                    <label className="text-sm font-bold text-slate-700 uppercase tracking-wider">Cantidad de gafas</label>
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-black text-blue-900" data-testid="quantity-display">{quantity}</span>
                      <span className="text-slate-500 font-semibold">unidades</span>
                    </div>
                  </div>

                  <input
                    type="range"
                    min="10"
                    max="150"
                    step="1"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value))}
                    data-testid="quantity-slider"
                    className="w-full h-3 bg-slate-200 rounded-full appearance-none cursor-pointer accent-blue-900"
                    style={{
                      background: `linear-gradient(to right, #1e3a8a 0%, #1e3a8a ${((quantity - 10) / 140) * 100}%, #e2e8f0 ${((quantity - 10) / 140) * 100}%, #e2e8f0 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-2 font-semibold">
                    <span>10</span>
                    <span>50</span>
                    <span>100</span>
                    <span>150</span>
                  </div>
                </div>

                {/* Summary */}
                <div className="grid md:grid-cols-3 gap-4 mb-8 pb-8 border-b border-slate-200">
                  <div className="text-center md:text-left">
                    <div className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-1">Precio unitario</div>
                    <div className="text-3xl font-black text-slate-900">${currentTier.unitPrice}</div>
                    {currentTier.discount > 0 && (
                      <div className="text-xs text-green-600 font-bold mt-1">
                        <span className="line-through text-slate-400 mr-1">$30</span>
                        Ahorras ${(30 - currentTier.unitPrice).toFixed(2)}/u
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-1">Descuento</div>
                    <div className="text-3xl font-black text-blue-900">{currentTier.discount}%</div>
                  </div>
                  <div className="text-center md:text-right">
                    <div className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-1">Total estimado</div>
                    <div className="text-3xl font-black text-blue-900" data-testid="total-price">${totalPrice}</div>
                    {parseFloat(savings) > 0 && (
                      <div className="text-xs text-green-600 font-bold mt-1">
                        Ahorro: ${savings}
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact name field */}
                <div className="mb-4">
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Tu nombre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    data-testid="quote-name-input"
                    placeholder="¿Cómo te llamas?"
                    className="w-full px-5 py-4 rounded-lg border-2 border-slate-200 focus:border-blue-900 focus:outline-none font-medium text-lg"
                  />
                </div>

                <button
                  onClick={handleWhatsAppContact}
                  disabled={!isFormValid}
                  data-testid="quote-whatsapp-btn"
                  className={`w-full py-5 rounded-lg font-bold text-lg flex items-center justify-center gap-3 transition-all ${
                    isFormValid
                      ? 'bg-green-500 text-white hover:bg-green-600 hover:shadow-2xl hover:scale-[1.02] cursor-pointer'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  <MessageCircle size={24} />
                  {isFormValid ? 'Pedir cotización por WhatsApp' : 'Ingresa tu nombre para continuar'}
                </button>

                <p className="text-center text-sm text-slate-500 mt-4">
                  📞 Atención lunes a viernes 9:00 - 18:00 · Respuesta en menos de 24 horas
                </p>

                {/* B2B Security Trust Strip */}
                <div className="mt-5 flex flex-wrap justify-center items-center gap-x-5 gap-y-2 pt-4 border-t border-slate-200">
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <ShieldCheck size={14} className="text-green-600" />
                    <span className="font-semibold">Cotización sin compromiso</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <BadgeCheck size={14} className="text-blue-900" />
                    <span className="font-semibold">Producto oficial FEF</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Building2 size={14} className="text-slate-500" />
                    <span className="font-semibold">Factura empresarial</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BENEFICIOS RÁPIDOS */}
        <section id="beneficios" className="py-20 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-14 max-w-3xl mx-auto">
              <div className="text-xs text-blue-900 font-bold tracking-[0.2em] uppercase mb-3">Beneficios clave</div>
              <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-4 tracking-tight">
                ¿Por qué elegirnos para tu próxima activación?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((b, idx) => (
                <div
                  key={idx}
                  data-testid={`benefit-${idx}`}
                  className="bg-slate-50 rounded-xl p-7 hover:bg-blue-50 hover:shadow-lg transition-all border border-slate-100 hover:border-blue-200"
                >
                  <div className="bg-blue-900 w-12 h-12 rounded-lg flex items-center justify-center mb-5">
                    <b.icon className="text-white" size={24} strokeWidth={2} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CASOS DE USO */}
        <section className="py-20 bg-gradient-to-br from-slate-900 to-blue-950 text-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-14">
                <div className="text-xs text-yellow-400 font-bold tracking-[0.2em] uppercase mb-3">Casos de uso</div>
                <h2 className="text-4xl lg:text-5xl font-black mb-4 tracking-tight">
                  Pensadas para tu
                  <span className="block text-yellow-400">próxima activación</span>
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                {useCases.map((uc, idx) => (
                  <div
                    key={idx}
                    data-testid={`usecase-${idx}`}
                    className="flex items-center gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-all"
                  >
                    <div className="bg-yellow-400 text-blue-950 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <uc.icon size={22} strokeWidth={2.5} />
                    </div>
                    <span className="text-lg font-semibold">{uc.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* TRUST - LAS GRANDES TIENDAS (con copy B2B adaptado) */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center mb-14">
              <div className="text-xs text-blue-900 font-bold tracking-[0.2em] uppercase mb-3">Respaldo comercial</div>
              <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-6 leading-tight tracking-tight">
                Una marca que ya está en
                <span className="block text-blue-900">las grandes cadenas</span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-4 max-w-3xl mx-auto">
                Las Gafas La Tri no son un experimento: ya están disponibles en puntos de venta seleccionados de Supermaxi, Megamaxi, AKÍ, Marathon Sports e islas Panini. Eso significa <strong>logística probada, producto confiable y respaldo real para tu empresa</strong>.
              </p>
              <p className="text-slate-500 italic">
                Lo que compras para tu marca es el mismo producto que ya eligen miles de hinchas en cadenas comerciales reconocidas.
              </p>
            </div>
          </div>
          <Trust hideHeader={true} />
        </section>

        {/* FORMULARIO DE CONTACTO */}
        <section id="contacto" className="py-20 bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 text-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <div className="text-xs text-yellow-400 font-bold tracking-[0.2em] uppercase mb-3">Formulario de contacto</div>
                <h2 className="text-4xl lg:text-5xl font-black mb-4 tracking-tight">
                  Solicita tu
                  <span className="block text-yellow-400">propuesta comercial</span>
                </h2>
                <p className="text-lg text-blue-100 leading-relaxed">
                  Déjanos tus datos y en menos de 24 horas un asesor se contactará contigo con una cotización personalizada.
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12 text-slate-900">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-bold mb-2">Nombre <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      data-testid="form-name"
                      placeholder="Tu nombre completo"
                      className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-blue-900 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2">Empresa</label>
                    <input
                      type="text"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      data-testid="form-company"
                      placeholder="Nombre de tu empresa"
                      className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-blue-900 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2">Cantidad estimada <span className="text-red-500">*</span></label>
                    <div className="flex items-center gap-3 bg-slate-50 px-4 py-3 rounded-lg border-2 border-slate-200">
                      <span className="text-2xl font-black text-blue-900">{quantity}</span>
                      <span className="text-slate-500 text-sm">unidades</span>
                      <button 
                        onClick={() => scrollToSection('volumen')}
                        className="ml-auto text-xs text-blue-900 font-bold hover:underline"
                      >
                        Ajustar ↑
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2">Ciudad</label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      data-testid="form-city"
                      placeholder="Ej. Quito, Guayaquil"
                      className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-blue-900 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2">Teléfono</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      data-testid="form-phone"
                      placeholder="+593 99 123 4567"
                      className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-blue-900 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2">Necesidad / Evento</label>
                    <input
                      type="text"
                      value={need}
                      onChange={(e) => setNeed(e.target.value)}
                      data-testid="form-need"
                      placeholder="Ej. Aniversario empresa"
                      className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-blue-900 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="mt-8">
                  <button
                    onClick={handleWhatsAppContact}
                    disabled={!isFormValid}
                    data-testid="form-submit"
                    className={`w-full py-5 rounded-lg font-bold text-lg flex items-center justify-center gap-3 transition-all ${
                      isFormValid
                        ? 'bg-green-500 text-white hover:bg-green-600 hover:shadow-xl hover:scale-[1.02] cursor-pointer'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    <MessageCircle size={22} />
                    Enviar solicitud por WhatsApp
                  </button>
                  <p className="text-center text-sm text-slate-500 mt-4">
                    Al enviar serás redirigido a WhatsApp con tu solicitud pre-armada
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer tagline */}
        <section className="py-12 bg-slate-100 border-t border-slate-200">
          <div className="container mx-auto px-4 lg:px-8 text-center">
            <div className="flex items-center justify-center gap-3 text-slate-600">
              <Sparkles size={18} className="text-blue-900" />
              <p className="text-base font-semibold">
                Gafas La Tri · Regalos corporativos que combinan utilidad, orgullo y diseño.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Empresas;
